import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { LogOut, Coins, User as UserIcon, Info, ChevronDown } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { useLang } from "../../contexts/LanguageContext";
import LanguageSelector from "./LanguageSelector";
import { WangramIcon } from "./WangramLogo";

export default function SearchHeader({ onNewSession }) {
  const { user, logout } = useAuth();
  const { t } = useLang();
  const credits = user?.credits ?? 0;
  const low = credits <= 2;
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return;
    const onDoc = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [menuOpen]);

  const initial = ((user?.name || user?.email || "?")[0] || "?").toUpperCase();

  return (
    <header className="glass-header sticky top-0 z-40" data-testid="app-header">
      <div className="flex items-center justify-between px-4 md:px-6 h-20">
        <button
          type="button"
          onClick={onNewSession}
          className="flex items-center gap-2 h-10 focus-ring rounded-md pr-3 -ml-1 pl-1 hover:bg-white/[0.03] transition-colors"
          aria-label={t("nav.newSession")}
          title={t("nav.newSession")}
          data-testid="logo-new-session-btn"
        >
          <WangramIcon className="w-7 h-7 rounded-md" />
          <div className="leading-tight text-left">
            <div className="font-display font-black text-[15px] tracking-tight">
              wangram<span className="text-[#2ADB80]">.com</span>
            </div>
            <div className="label-eyebrow" style={{ fontSize: 9 }}>{t("brand.tagline")}</div>
          </div>
        </button>

        <div className="flex items-center gap-3 h-10">
          <LanguageSelector />

          <Link
            to="/pricing"
            className={`btn-ghost focus-ring ${low ? "border-[#ff9c85]/40 text-[#ffb097]" : ""}`}
            data-testid="credit-balance-btn"
            title={t("nav.buyCoins")}
          >
            <Coins className="w-3.5 h-3.5" />
            <span className="font-mono-p text-[12px]">
              <span data-testid="credit-balance-value">{credits}</span>
              <span className="text-white/40"> {t("nav.coins")}</span>
            </span>
          </Link>

          <div className="relative" ref={menuRef}>
            <button
              onClick={() => setMenuOpen((v) => !v)}
              className="flex items-center gap-2 h-9 pl-1 pr-2.5 rounded-full border border-white/10 hover:bg-white/[0.05] transition-colors focus-ring"
              data-testid="user-menu-btn"
              aria-haspopup="menu"
              aria-expanded={menuOpen}
            >
              <span className="w-7 h-7 rounded-full bg-white/10 border border-white/15 flex items-center justify-center font-display font-black text-[11px]">
                {initial}
              </span>
              <ChevronDown className={`w-3.5 h-3.5 text-white/50 transition-transform ${menuOpen ? "rotate-180" : ""}`} />
            </button>

            {menuOpen && (
              <div
                className="absolute right-0 top-11 w-64 rounded-lg border border-white/10 bg-[#0F0F0F] shadow-2xl py-2 fade-in"
                data-testid="user-menu-dropdown"
                role="menu"
              >
                <div className="px-4 py-3 border-b border-white/10">
                  <div className="text-[13px] font-medium text-white/90 truncate" data-testid="menu-user-name">
                    {user?.name || user?.email?.split("@")[0]}
                  </div>
                  <div className="text-[11px] text-white/50 font-mono-p truncate" data-testid="menu-user-email">
                    {user?.email}
                  </div>
                </div>
                <Link
                  to="/profile"
                  onClick={() => setMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-2.5 text-[13px] text-white/85 hover:bg-white/5 transition-colors"
                  data-testid="menu-profile-link"
                  role="menuitem"
                >
                  <UserIcon className="w-3.5 h-3.5 text-white/60" />
                  {t("nav.profile")}
                </Link>
                <Link
                  to="/about"
                  onClick={() => setMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-2.5 text-[13px] text-white/85 hover:bg-white/5 transition-colors"
                  data-testid="menu-about-link"
                  role="menuitem"
                >
                  <Info className="w-3.5 h-3.5 text-white/60" />
                  {t("nav.about")}
                </Link>
                <Link
                  to="/pricing"
                  onClick={() => setMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-2.5 text-[13px] text-white/85 hover:bg-white/5 transition-colors"
                  data-testid="menu-pricing-link"
                  role="menuitem"
                >
                  <Coins className="w-3.5 h-3.5 text-white/60" />
                  {t("nav.buy")}
                </Link>
                <div className="border-t border-white/10 my-1" />
                <button
                  onClick={() => { setMenuOpen(false); logout(); }}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-[13px] text-white/85 hover:bg-white/5 transition-colors text-left"
                  data-testid="menu-logout-btn"
                  role="menuitem"
                >
                  <LogOut className="w-3.5 h-3.5 text-white/60" />
                  {t("nav.signOut")}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
